import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

import { KeyValueUserModel } from '@models/common';
import { NotifyEmailsAndRolesModel } from '@module-instance/dynamic-module/common';
import { ModuleInstanceCommonService } from '@module-instance/services/module-instance-common.service';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { CipoCheckboxComponent } from '@shared/components/fields/cipo-checkbox/cipo-checkbox.component';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoCheckboxControl } from '@shared/components/fields/common';
import { UserStore } from '@signal-store/user.store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { finalize } from 'rxjs';

@Component({
  selector: 'cipo-undo-transition-dialog',
  templateUrl: './undo-transition-dialog.component.html',
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    TranslateModule,
    FlexLayoutModule,
    CipoSelectComponent,
    CipoCheckboxComponent,
    NgxSkeletonLoaderModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UndoTransitionDialog {
  readonly data = inject<UndoTransitionDialogData>(MAT_DIALOG_DATA);
  moduleInstanceCommon = inject(ModuleInstanceCommonService);
  userStore = inject(UserStore);

  form = new FormGroup({
    notify: new CipoCheckboxControl(false, {
      label: 'dm.undoLastAction.notifyUsers',
    }),
    notifyUsers: new FormControl<string[]>({ value: null, disabled: true }),
  });

  notifyUsersConfig = computed(() => ({
    label: 'roles.notify',
    multiple: true,
    allSelectedLabel: 'general.allSelected',
    toggleAllOutside: true,
    options: this.userRoles(),
    hideInUse: true,
  }));

  contractId = computed(() => this.userStore.contract()?.id);
  userRoles = signal<KeyValueUserModel<string>[]>([]);
  rolesLoading = signal(true);
  usedUserRoles = computed(() => {
    const userRoles = this.userRoles();
    return userRoles.filter(r => r.isUsed);
  });

  constructor() {
    this.moduleInstanceCommon
      .getUserRoleEmails(this.data.moduleId, this.data.entityInstanceId, this.data.stateId, this.contractId())
      .pipe(finalize(() => this.rolesLoading.set(false)))
      .subscribe(userRoleEmails => {
        this.userRoles.set(userRoleEmails);

        if (userRoleEmails.length === 0) {
          this.form.controls.notify.setValue(false, { emitEvent: false });
          this.form.controls.notify.disable({ emitEvent: false });
        }
      });

    this.form.controls.notify.valueChanges.subscribe(notifyEnabled => {
      if (notifyEnabled) {
        // Restore previously selected roles
        this.form.controls.notifyUsers.setValue(this.usedUserRoles().map(r => r.key));
        this.form.controls.notifyUsers.enable();
      } else {
        // Store current selection before clearing
        this.form.controls.notifyUsers.setValue([]);
        this.form.controls.notifyUsers.disable();
      }
    });
  }

  getDialogResult() {
    const notifyEnabled = this.form.controls.notify.value;
    return {
      confirmed: true,
      notifyUsers: notifyEnabled
        ? {
            emails: this.form.controls.notifyUsers.value.filter(e => !Number(e)),
            groups: this.form.controls.notifyUsers.value.filter(e => Number(e)),
          }
        : { emails: [], groups: [] },
    };
  }
}
export interface UndoTransitionDialogData {
  moduleId: number;
  stateId?: number;
  entityInstanceId: number;
  title: string;
  message?: string;
}
export interface UndoTransitionDialogResult {
  confirmed: boolean;
  notifyUsers: NotifyEmailsAndRolesModel;
}
